// supervisor-directive-processing.ts
//
// P7 tests 25-28: called once per tick BOUNDARY (never mid-attempt) by
// supervisor-tick-boundary-directive-drain.ts (which supervisor-run-until-idle.ts
// runs every tick), before the next attempt is dispatched.
// Scans the inbox for unprocessed redirect/revert directives and routes
// each ticket by shape (phase 08, Req 1/4, D3):
//   - `redirect` with non-empty `payload.text` and NO `proposed_changes` ->
//     materializeRedirectTextAsDecisionTicket (F3: the operator's free-text
//     instruction becomes a visible `kind:"decision"` ticket instead of
//     being silently dropped — the old bug, since RedirectDirectivePayload
//     never had a `text` field for applyRedirectDirective to read).
//   - `revert` -> rejected outright (revert-v1 is a controller executor with
//     no dispatch path in v1 — see autopilot-control-directive-commands.ts's
//     `revert` action for the CLI-side rejection of the same shape).
//   - everything else (proposed_changes / supersede / invalidate_gate_ids) ->
//     applyRedirectDirective (F2, phase 07).
//
// Phase 07 (D4/N2): a REJECTED apply (bad proposal, stale revision, ...) is
// a decided outcome, not a system fault — it is journaled as
// `directive_rejected`, notified, and marked processed here, same tick,
// instead of throwing. The old behaviour threw and left the directive
// unmarked, so every following tick re-hit the identical rejection and
// crashed `run` again (mission stuck forever, loài R-01).
//
// B4 (plan 20260915-1650 phase-04, 2026-09-16): `answer` was REMOVED from
// this set. The old comment claimed an answer "resolves to a ticket-
// transaction like the other two" — measured false: the parser
// (`mattermost-command-body-parser.ts:66`) builds `{ticket, request_id,
// text}`, while `RedirectDirectivePayload` only reads `proposed_changes` /
// `supersede_done_ticket_ids` / `invalidate_gate_ids`. No field ever
// received `text`, so every answer was durably written, marked `.processed`,
// and silently discarded. Answers now drain through
// interview-answer-directive-processing.ts into interview.json.
//
// The two drainers MUST stay disjoint: they share one `.processed` marker
// namespace, so a kind listed in both would be claimed by whichever ran
// first and skipped by the other — the same silent drop, as a race. Pinned
// by answer-directive-not-consumed-by-redirect-path.test.ts.

import { randomUUID } from "node:crypto";
import type { GitRunner } from "./commit-owner.js";
import {
  isDirectiveProcessed,
  listInboxDirectiveIds,
  markDirectiveProcessed,
  readDirective,
} from "./directive-inbox.js";
import {
  applyRedirectDirective,
  type RedirectDirectivePayload,
} from "./directive-redirect-application.js";
import { retirePendingNeedsDecisionQuestions } from "./interview-retire-ticket-questions.js";
import type { MissionStore } from "./mission-store.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";
import {
  isRedirectTextOnlyDirective,
  materializeRedirectTextAsDecisionTicket,
} from "./redirect-text-to-decision-ticket.js";
import { loadTicketsFromRepo } from "./supervisor-ticket-projection.js";

const REDIRECT_LIKE_KINDS = new Set(["redirect", "revert"]);

/** Req 4 (D3): revert-v1 is a controller executor with no dispatch path in v1. */
const REVERT_REJECTION_REASON = "revert_not_supported_v1";

export interface ProcessPendingRedirectDirectivesInput {
  store: MissionStore;
  controlRoot: string;
  repoDir: string;
  missionId: string;
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  /** Artifact identity (e.g. HEAD sha) gates should be invalidated against. */
  invalidationArtifactHash: string;
  /** Operator-facing line for a rejected redirect (D4: reject is non-fatal). */
  notify?: (line: string) => void;
}

export interface ProcessPendingRedirectDirectivesResult {
  processedDirectiveIds: string[];
}

/**
 * Drains every unprocessed redirect/answer/revert directive currently in
 * the inbox. Safe to call every tick: already-processed directives are
 * skipped via the `.processed` marker (directive-inbox.ts), and each
 * apply is itself idempotent by operation_id (directive-redirect-
 * application.ts), so a crash between "apply committed" and "marker
 * written" only risks re-marking, never re-applying.
 */
export async function processPendingRedirectDirectives(
  input: ProcessPendingRedirectDirectivesInput,
): Promise<ProcessPendingRedirectDirectivesResult> {
  const ids = await listInboxDirectiveIds(input.controlRoot);
  const processedDirectiveIds: string[] = [];

  // D4/N2: a REJECTED directive is a DECIDED outcome (bad proposal, stale
  // revision, unsupported kind, ...), not a system fault — journal it (same
  // directive_rejected shape supervisor-halt-directive-step.ts's
  // journalRejected uses for approve/abort rejections), mark it processed so
  // it is never retried, and tell the operator. The old `throw` left the
  // directive unmarked, so every following tick re-hit the identical
  // rejection and crashed `run` again (mission stuck forever, loài R-01).
  const journalRejected = async (directiveId: string, kind: string, reason: string) => {
    await input.store.commit([
      {
        event_id: randomUUID(),
        delta: {
          kind: "directive_rejected",
          directive_id: directiveId,
          directive_kind: kind,
          reason,
        },
      },
    ]);
    input.notify?.(`Redirect ${directiveId} bị từ chối: ${reason}`);
  };

  for (const directiveId of ids) {
    const directive = await readDirective(input.controlRoot, directiveId);
    if (directive === undefined) continue;
    if (!REDIRECT_LIKE_KINDS.has(directive.kind)) continue;
    if (await isDirectiveProcessed(input.controlRoot, directiveId)) continue;

    // Req 4 (D3): `revert` never had a real dispatch path in v1 — reject
    // outright instead of silently discarding (N1, the old no-op bug).
    if (directive.kind === "revert") {
      await journalRejected(directiveId, directive.kind, REVERT_REJECTION_REASON);
      await markDirectiveProcessed(input.controlRoot, directiveId);
      processedDirectiveIds.push(directiveId);
      continue;
    }

    const tickets = await loadTicketsFromRepo(input.repoDir);

    // Req 1 (F3): free-text redirect with NO proposed_changes materializes a
    // `kind:"decision"` ticket instead of going through applyRedirectDirective
    // (which only ever read proposed_changes/supersede/invalidate_gate_ids —
    // `text` was silently dropped). Branch decided BEFORE calling materialize
    // (not on its return value) so a crash-then-replay that hits the
    // idempotent early-return inside materialize never falls through to F2.
    if (isRedirectTextOnlyDirective(directive)) {
      const materialized = await materializeRedirectTextAsDecisionTicket({
        store: input.store,
        repoDir: input.repoDir,
        directive,
        tickets,
        gitRunner: input.gitRunner,
        evaluatePolicy: input.evaluatePolicy,
        missionId: input.missionId,
        notify: input.notify,
      });
      if (materialized.commitFailedReason !== undefined) {
        await journalRejected(directiveId, directive.kind, materialized.commitFailedReason);
      }
      await markDirectiveProcessed(input.controlRoot, directiveId);
      processedDirectiveIds.push(directiveId);
      continue;
    }

    const payload = directive.payload as RedirectDirectivePayload;
    const result = await applyRedirectDirective({
      store: input.store,
      controlRoot: input.controlRoot,
      repoDir: input.repoDir,
      directiveId,
      directiveSource: directive.source,
      tickets,
      ticketStatus: input.store.state.tickets,
      payload,
      invalidationArtifactHash: input.invalidationArtifactHash,
      // Re-review 2026-09-26 (F-B1/F-B2): ticket files written by the redirect
      // are committed in the same tick, and DAG policy warnings reach the operator.
      gitRunner: input.gitRunner,
      evaluatePolicy: input.evaluatePolicy,
      missionId: input.missionId,
      notify: input.notify,
    });
    if (!result.ok) {
      await journalRejected(directiveId, directive.kind, result.reason);
    } else {
      // 06h R1 (B1/N10): targets from the PAYLOAD (replay-safe), not `touchedTicketIds`;
      // 06i R1 (B9): the payload's amend patch may also ANSWER the question.
      await retirePendingNeedsDecisionQuestions({
        store: input.store,
        controlRoot: input.controlRoot,
        payload,
        directiveId,
      });
    }
    await markDirectiveProcessed(input.controlRoot, directiveId);
    processedDirectiveIds.push(directiveId);
  }

  return { processedDirectiveIds };
}
